import * as React from "react";
import { FormattedMessage } from "react-intl";
import NewsFeedItemContainer from "components/news/NewsFeedItemContainer/NewsFeedItemContainer";
import { NewsFeed, NewsFeedItem } from "interfaces/content";
import { sortNewsFeedItems } from "screens/common/FrontPage/FrontPage";
import styled from "styled-components";
import LoadingIndicator from "../../ui/LoadingIndicator/LoadingIndicator";
import { parseISO, differenceInDays } from "date-fns";
import Collapsible from "components/common/ui/Collapsible/Collapsible";

const recentWithinDays = 30; // days
const cutoffAfterDays = 180;

export interface Props {
    faultNotifications: NewsFeed | null;
    isLoading: boolean;
    closeModal?: () => void;
    displayOnlyRecent: boolean;
}

const Container = styled.div`
    position: relative;
`;

const today = new Date();

const isRecentFaultNotification = (item: NewsFeedItem) =>
    item.isActive ||
    Math.abs(differenceInDays(today, parseISO(item.publishingDate))) <= recentWithinDays;

const FaultNotificationsFeed = (props: Props) => {
    const { faultNotifications, isLoading, closeModal, displayOnlyRecent } = props;

    if (isLoading) {
        return <Container>{isLoading && <LoadingIndicator isLoading={isLoading} />}</Container>;
    }

    if (!faultNotifications) {
        return null;
    }

    if (faultNotifications.newsFeedItems.length === 0) {
        return <FormattedMessage id="newsfeed.newsNotFound" />;
    }

    const [recentItems, olderItems] = sortNewsFeedItems(faultNotifications.newsFeedItems).reduce(
        ([recentItems, olderItems], item) => {
            if (isRecentFaultNotification(item)) {
                return [[...recentItems, item], olderItems];
            }

            return [recentItems, [...olderItems, item]];
        },
        [[], []] as [NewsFeedItem[], NewsFeedItem[]]
    );

    const displayableOlderItems = olderItems.filter(
        (item) =>
            Math.abs(differenceInDays(today, parseISO(item.publishingDate))) <= cutoffAfterDays
    );

    return (
        <Container>
            <Collapsible
                excerpt={
                    <>
                        {recentItems.map((newsFeedItem, index) => (
                            <NewsFeedItemContainer
                                key={index}
                                newsFeedItem={newsFeedItem}
                                previewMode={true}
                                closeModal={closeModal}
                            />
                        ))}
                    </>
                }
                collapsed={displayOnlyRecent}
            >
                <>
                    {displayableOlderItems.map((newsFeedItem, index) => (
                        <NewsFeedItemContainer
                            key={index}
                            newsFeedItem={newsFeedItem}
                            previewMode={true}
                            closeModal={closeModal}
                        />
                    ))}
                </>
            </Collapsible>
        </Container>
    );
};

export default FaultNotificationsFeed;
